export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const PORTABILITY_PRODUCTION_API_ORIGIN = "https://api.portabilityapp.net";

export interface GooglePlaySandboxCertificationIdentity {
  readonly applicationId: "com.portability.app";
  readonly androidVersionCode: number;
  readonly track: "internal";
}

let googlePlaySandboxCertificationIdentity: GooglePlaySandboxCertificationIdentity | null = null;

export function configureGooglePlaySandboxCertificationIdentity(identity: GooglePlaySandboxCertificationIdentity | null): void {
  if (identity !== null) {
    if (identity.applicationId !== "com.portability.app") throw new Error("Google Play sandbox certification requires the Portability application id");
    if (!Number.isSafeInteger(identity.androidVersionCode) || identity.androidVersionCode < 1) throw new Error("Google Play sandbox certification requires a positive Android version code");
    if (identity.track !== "internal") throw new Error("Google Play sandbox certification is restricted to Internal Testing");
  }
  googlePlaySandboxCertificationIdentity = identity === null ? null : Object.freeze({ ...identity });
}

type NativeSessionBridge = {
  readSessionToken?(input?: { readonly requireDeviceOwnerAuthentication?: boolean; readonly reason?: string }): Promise<{ readonly sessionToken?: string | null }>;
  writeSessionToken?(input: { readonly sessionToken: string; readonly requireDeviceOwnerAuthentication?: boolean }): Promise<void>;
  clearSessionToken?(): Promise<void>;
};

function nativeShell(location: Pick<Location, "protocol" | "hostname" | "port"> | undefined = globalThis.location): boolean {
  if (location === undefined) return false;
  const capacitor = (globalThis as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return location.protocol === "capacitor:" || location.protocol === "ionic:" || capacitor?.isNativePlatform?.() === true;
}

function nativeSessionBridge(): NativeSessionBridge | null {
  const candidate = (globalThis as unknown as { Capacitor?: { Plugins?: { PortabilitySecureSession?: NativeSessionBridge } } }).Capacitor?.Plugins?.PortabilitySecureSession;
  return candidate ?? null;
}

function biometricLockEnabled(): boolean {
  try {
    const raw = globalThis.localStorage?.getItem("portability.mobile.preferences.v1");
    if (raw === null || raw === undefined) return false;
    const parsed = JSON.parse(raw) as { readonly biometricLock?: unknown };
    return parsed.biometricLock === true;
  } catch {
    return false;
  }
}

async function readNativeSessionToken(): Promise<string | null> {
  if (!nativeShell()) return null;
  const bridge = nativeSessionBridge();
  if (bridge?.readSessionToken !== undefined) {
    const requireDeviceOwnerAuthentication = biometricLockEnabled();
    try {
      const value = await bridge.readSessionToken({
        requireDeviceOwnerAuthentication,
        reason: "Unlock Portability session"
      });
      return typeof value.sessionToken === "string" && value.sessionToken.trim() ? value.sessionToken : null;
    } catch (error) {
      // A fresh install has no native session yet. Treat secure-storage read
      // failures as anonymous unless the user explicitly enabled biometric
      // session protection, where failing closed is required.
      if (requireDeviceOwnerAuthentication) throw error;
      return null;
    }
  }
  try {
    return globalThis.sessionStorage?.getItem("portability.nativeSessionToken") ?? null;
  } catch {
    return null;
  }
}

async function writeNativeSessionToken(value: string | null): Promise<void> {
  if (!nativeShell()) return;
  const bridge = nativeSessionBridge();
  if (value === null) {
    if (bridge?.clearSessionToken !== undefined) {
      await bridge.clearSessionToken();
      return;
    }
    try {
      globalThis.sessionStorage?.removeItem("portability.nativeSessionToken");
    } catch {}
    return;
  }
  if (bridge?.writeSessionToken !== undefined) {
    await bridge.writeSessionToken({ sessionToken: value, requireDeviceOwnerAuthentication: biometricLockEnabled() });
    return;
  }
  try {
    globalThis.sessionStorage?.setItem("portability.nativeSessionToken", value);
  } catch {}
}

function browserCookieValue(name: string): string | null {
  try {
    if (typeof document === "undefined" || typeof document.cookie !== "string") return null;
    for (const part of document.cookie.split(";")) {
      const index = part.indexOf("=");
      if (index < 0) continue;
      if (part.slice(0, index).trim() === name) return decodeURIComponent(part.slice(index + 1).trim());
    }
  } catch {}
  return null;
}

function unsafeMethod(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

export function resolveApiRequestUrl(path: string, location: Pick<Location, "origin" | "protocol" | "hostname" | "port"> | undefined = globalThis.location): string {
  if (!path.startsWith("/") || path.startsWith("//")) throw new Error("Portability API paths must be root-relative");
  if (location === undefined) return path;
  return new URL(path, nativeShell(location) ? PORTABILITY_PRODUCTION_API_ORIGIN : location.origin).href;
}

export function qualificationAccountFromLocation(): string | null {
  const account = new URLSearchParams(globalThis.location.search).get("qualificationAccount");
  return account?.trim() || null;
}

export async function apiRequest<T>(path: string, options: {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: object;
  readonly qualificationAccount?: string | null | undefined;
  readonly sessionToken?: string | null | undefined;
} = {}): Promise<T> {
  const headers = new Headers({ accept: "application/json" });
  if (options.body !== undefined) headers.set("content-type", "application/json");
  if (options.qualificationAccount) headers.set("x-portability-test-account", options.qualificationAccount);
  if (googlePlaySandboxCertificationIdentity !== null) {
    headers.set("x-portability-application-id", googlePlaySandboxCertificationIdentity.applicationId);
    headers.set("x-portability-android-version-code", `${googlePlaySandboxCertificationIdentity.androidVersionCode}`);
    headers.set("x-portability-google-play-track", googlePlaySandboxCertificationIdentity.track);
  }
  const sessionToken = options.sessionToken ?? await readNativeSessionToken();
  if (sessionToken) headers.set("authorization", `Bearer ${sessionToken}`);
  const method = options.method ?? "GET";
  if (!sessionToken && unsafeMethod(method)) {
    const csrf = browserCookieValue("__Host-portability_csrf");
    if (csrf !== null) headers.set("x-csrf-token", csrf);
  }
  const response = await fetch(resolveApiRequestUrl(path), {
    method,
    headers,
    credentials: "include",
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) })
  });
  let value: unknown = null;
  const responseText = await response.text();
  if (responseText.trim()) {
    try {
      value = JSON.parse(responseText) as unknown;
    } catch {
      throw new ApiError(
        response.status,
        response.ok
          ? "Portability received an invalid server response. Please try again."
          : `Portability is temporarily unavailable (${response.status}). Please try again.`
      );
    }
  }
  if (!response.ok) {
    const message = typeof value === "object" && value !== null && "error" in value
      ? typeof value.error === "string"
        ? value.error
        : typeof value.error === "object" && value.error !== null && "message" in value.error && typeof value.error.message === "string"
          ? value.error.message
          : `Request failed (${response.status})`
      : `Request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }
  if (typeof value === "object" && value !== null && "sessionToken" in value && typeof value.sessionToken === "string" && value.sessionToken.trim()) {
    await writeNativeSessionToken(value.sessionToken);
  }
  if (path === "/v1/auth/signout") await writeNativeSessionToken(null);
  return value as T;
}
